import {
  PAYMENT_METHODS,
  dec,
  formatDate,
  formatDateTime,
  moneyToString,
  type DocumentDetail,
} from '@fdt/shared';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockIcon from '@mui/icons-material/Block';
import PaymentIcon from '@mui/icons-material/PaymentsOutlined';
import PersonIcon from '@mui/icons-material/PersonOutline';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { errorKey } from '../api/client';
import { FormDialog } from '../components/FormDialog';
import { MoneyText } from '../components/MoneyText';
import { useFormError } from '../hooks/useFormError';
import { currentLocale } from '../i18n';
import { documentsApi, paymentsApi } from '../resources/documents';

/** `YYYY-MM-DD` for today in Europe/Rome. */
function todayInRome(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function DocumentDetailPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();

  const document = useQuery({
    queryKey: ['documents', id],
    queryFn: () => documentsApi.get(id),
    enabled: Boolean(id),
  });

  const docPayments = useQuery({
    queryKey: ['payments', { documentId: id }],
    queryFn: () => paymentsApi.list({ page: 1, pageSize: 50, documentId: id }),
    enabled: Boolean(id),
  });

  function refresh(updated?: DocumentDetail): void {
    if (updated) {
      queryClient.setQueryData(['documents', id], updated);
    }
    void queryClient.invalidateQueries({ queryKey: ['documents'] });
    void queryClient.invalidateQueries({ queryKey: ['payments'] });
    void queryClient.invalidateQueries({ queryKey: ['customers'] });
  }

  // --- cancel ---
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const cancelError = useFormError();
  const cancel = useMutation({
    mutationFn: () => documentsApi.cancel(id, { reason: cancelReason }),
    onSuccess: (updated) => {
      setCancelOpen(false);
      refresh(updated);
    },
    onError: (error) => cancelError.capture(error),
  });

  // --- payment ---
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<(typeof PAYMENT_METHODS)[number]>('cash');
  const [payDate, setPayDate] = useState(todayInRome());
  const [payNotes, setPayNotes] = useState('');
  const payError = useFormError();
  const pay = useMutation({
    mutationFn: () =>
      paymentsApi.create({
        documentId: id,
        amount: payAmount,
        method: payMethod,
        paidOn: payDate,
        notes: payNotes || null,
      }),
    onSuccess: () => {
      setPayOpen(false);
      refresh();
      void document.refetch();
    },
    onError: (error) => payError.capture(error),
  });

  if (document.isPending) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (document.isError || !document.data) {
    return <Alert severity="error">{t(errorKey(document.error))}</Alert>;
  }

  const detail = document.data;
  const outstanding = dec(detail.grossTotal).minus(dec(detail.paidTotal));
  const cancelled = detail.status === 'cancelled';

  return (
    <Stack spacing={2} sx={{ maxWidth: 1100 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton onClick={() => navigate('/documents')} aria-label={t('common.back')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h1">
          {t(`documents.types.${detail.type}`)} {detail.number}
        </Typography>
        {cancelled ? (
          <Chip color="error" variant="outlined" label={t('documents.cancelled')} />
        ) : (
          <Chip color="success" label={t('documents.active')} />
        )}
      </Stack>

      {cancelled && (
        <Alert severity="warning">
          {t('documents.cancelledNotice', {
            at: detail.cancelledAt ? formatDateTime(detail.cancelledAt, locale) : '',
            reason: detail.cancellationReason ?? '',
          })}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <Card sx={{ flexGrow: 1, width: '100%' }}>
          <CardContent>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
            >
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('fields.businessName')}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body1">
                    {detail.customerCode} — {detail.customerName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/customers/${detail.customerId}/account`)}
                    aria-label={t('documents.openCustomer')}
                  >
                    <PersonIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('fields.date')}
                </Typography>
                <Typography variant="body1">
                  {formatDate(`${detail.issuedOn}T00:00:00Z`, locale)}
                </Typography>
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('documents.issuedBy')}
                </Typography>
                <Typography variant="body1">
                  {detail.createdByUserName ?? '—'}
                  {detail.vehiclePlate ? ` · ${detail.vehiclePlate}` : ''}
                </Typography>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('fields.sku')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('fields.product')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('documents.lineKind')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {t('fields.quantity')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {t('documents.unitPrice')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {t('fields.vatRate')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {t('documents.lineNet')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{line.productSku}</TableCell>
                    <TableCell>{line.productName}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={line.kind === 'return' ? 'secondary' : 'default'}
                        label={t(`documents.lineKinds.${line.kind}`)}
                      />
                      {line.returnReason && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t(`documents.returnReasons.${line.returnReason}`)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Number.parseFloat(line.quantity).toLocaleString(
                        locale === 'it' ? 'it-IT' : 'tr-TR',
                        { maximumFractionDigits: 3 },
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <MoneyText value={line.unitPrice} variant="body2" />
                    </TableCell>
                    <TableCell align="right">{line.vatRate}%</TableCell>
                    <TableCell align="right">
                      {/* A return line subtracts, so it is shown negative. */}
                      <MoneyText
                        value={
                          line.kind === 'return'
                            ? moneyToString(dec(line.lineNet).negated())
                            : line.lineNet
                        }
                        variant="body2"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Stack spacing={2} sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h2" gutterBottom>
                {t('documents.totals')}
              </Typography>

              <Table size="small">
                <TableBody>
                  {detail.vatGroups.map((group) => (
                    <TableRow key={group.vatRate}>
                      <TableCell sx={{ border: 0, py: 0.5 }}>
                        {t('documents.vatGroup', { rate: group.vatRate })}
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.5 }}>
                        <MoneyText value={group.net} variant="body2" />
                      </TableCell>
                      <TableCell align="right" sx={{ border: 0, py: 0.5 }}>
                        <MoneyText value={group.vat} variant="body2" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('documents.net')}</Typography>
                  <MoneyText value={detail.netTotal} variant="body2" />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('documents.vat')}</Typography>
                  <MoneyText value={detail.vatTotal} variant="body2" />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {t('documents.gross')}
                  </Typography>
                  <MoneyText value={detail.grossTotal} sx={{ fontWeight: 700 }} />
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('documents.paid')}</Typography>
                  <MoneyText value={detail.paidTotal} variant="body2" />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('documents.outstanding')}
                  </Typography>
                  <MoneyText value={moneyToString(outstanding)} signed sx={{ fontWeight: 600 }} />
                </Stack>
              </Stack>

              <Stack spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  disabled={cancelled || outstanding.lessThanOrEqualTo(0)}
                  onClick={() => {
                    setPayAmount(moneyToString(outstanding));
                    setPayDate(todayInRome());
                    setPayNotes('');
                    payError.clear();
                    setPayOpen(true);
                  }}
                >
                  {t('documents.recordPayment')}
                </Button>
                <Button
                  color="error"
                  startIcon={<BlockIcon />}
                  disabled={cancelled}
                  onClick={() => {
                    setCancelReason('');
                    cancelError.clear();
                    setCancelOpen(true);
                  }}
                >
                  {t('documents.cancel')}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h2" gutterBottom>
                {t('nav.payments')}
              </Typography>
              {(docPayments.data?.items ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('documents.noPayments')}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {(docPayments.data?.items ?? []).map((payment) => (
                    <Paper key={payment.id} variant="outlined" sx={{ p: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack spacing={0}>
                          <Typography variant="body2">
                            {t(`payments.methods.${payment.method}`)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(`${payment.paidOn}T00:00:00Z`, locale)}
                            {payment.cancelledAt ? ` · ${t('documents.cancelled')}` : ''}
                          </Typography>
                        </Stack>
                        <MoneyText
                          value={payment.amount}
                          variant="body2"
                          sx={{
                            textDecoration: payment.cancelledAt ? 'line-through' : undefined,
                            opacity: payment.cancelledAt ? 0.6 : 1,
                          }}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Stack>

      <FormDialog
        open={cancelOpen}
        title={t('documents.cancelTitle')}
        onClose={() => setCancelOpen(false)}
        onSubmit={() => cancel.mutate()}
        submitting={cancel.isPending}
        errorKey={cancelError.errorKey}
        submitLabel={t('documents.cancel')}
        maxWidth="xs"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">{t('documents.cancelNotice')}</Alert>
          <TextField
            label={t('fields.reason')}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            required
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </FormDialog>

      <FormDialog
        open={payOpen}
        title={t('documents.paymentTitle')}
        onClose={() => setPayOpen(false)}
        onSubmit={() => pay.mutate()}
        submitting={pay.isPending}
        errorKey={payError.errorKey}
        maxWidth="xs"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('documents.amount')}
            value={payAmount}
            onChange={(event) => setPayAmount(event.target.value)}
            helperText={t('documents.outstandingHelp', {
              amount: moneyToString(outstanding),
            })}
            inputMode="decimal"
            required
            fullWidth
          />
          <TextField
            select
            label={t('payments.method')}
            value={payMethod}
            onChange={(event) =>
              setPayMethod(event.target.value as (typeof PAYMENT_METHODS)[number])
            }
            fullWidth
          >
            {PAYMENT_METHODS.map((method) => (
              <MenuItem key={method} value={method}>
                {t(`payments.methods.${method}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('fields.date')}
            type="date"
            value={payDate}
            onChange={(event) => setPayDate(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            required
            fullWidth
          />
          <TextField
            label={t('fields.notes')}
            value={payNotes}
            onChange={(event) => setPayNotes(event.target.value)}
            fullWidth
          />
        </Stack>
      </FormDialog>
    </Stack>
  );
}
